/**
 * Tiny external store for the local-launcher probe result.
 *
 * One driver (`useLauncherProbeDriver`, mounted once in the outer reconnect
 * harness) polls the launcher's `GET /status` while the STDB-derived state
 * says "launcher not running", and publishes the result here. Any number of
 * consumers (the topbar LauncherButton, the per-row `useLauncherReadiness`
 * instances on TraineePortal) read it via `useLauncherProbe()` without each
 * spawning their own polling loop.
 */

import { useSyncExternalStore } from 'react';

export type LauncherProbe = {
  /** 'idle' = not currently probing (STDB state is green or red);
   *  'unreachable' = probed, no local launcher answered;
   *  'running' = the local API responded. */
  status: 'idle' | 'unreachable' | 'running';
  /** Signed-in user of the running launcher; null when unknown (legacy
   *  launcher build without `user_email`, or a service token). */
  email: string | null;
  sub: string | null;
  version: string | null;
  probedAt: number;
};

const IDLE: LauncherProbe = {
  status: 'idle',
  email: null,
  sub: null,
  version: null,
  probedAt: 0,
};

let snapshot: LauncherProbe = IDLE;
const listeners = new Set<() => void>();

export function setLauncherProbe(next: LauncherProbe): void {
  snapshot = next;
  listeners.forEach((l) => l());
}

export function resetLauncherProbe(): void {
  if (snapshot !== IDLE) setLauncherProbe(IDLE);
}

export function getLauncherProbe(): LauncherProbe {
  return snapshot;
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Latest local-launcher probe result (see `useLauncherProbeDriver`). */
export function useLauncherProbe(): LauncherProbe {
  return useSyncExternalStore(subscribe, getLauncherProbe, getLauncherProbe);
}
